<template>
    <div class="cancel-status-backdrop">
        <div class="cancel-status-card" :class="{ 'is-success': status === 'success' }">

            <div v-if="status === 'loading'" class="cancel-loading">
                <div class="cancel-spinner"></div>
                <p class="cancel-message">Cancelling your voucher...</p>
            </div>

            <div v-else class="cancel-success">
                <div ref="lottieContainer" class="cancel-video"></div>
                <p class="cancel-message">Voucher cancelled successfully!</p>
                <button type="button" class="cancel-close" @click="$emit('close')">
                    Close
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import lottie from 'lottie-web';

export default {
    name: 'VoucherCancelStatusModal',
    props: {
        status: {
            type: String,
            default: 'loading',
            validator: value => ['loading', 'success'].includes(value)
        }
    },
    emits: ['close'],
    data() {
        return {
            animInstance: null
        };
    },
    mounted() {
        document.body.style.overflow = 'hidden';
        if (this.status === 'success') {
            this.initLottie();
        }
    },
    beforeUnmount() {
        document.body.style.overflow = '';
        this.destroyLottie();
    },
    watch: {
        status(newValue) {
            if (newValue === 'success') {
                this.$nextTick(() => {
                    this.initLottie();
                });
            }
        }
    },
    methods: {
        async initLottie() {
            this.destroyLottie();
            await this.$nextTick();

            const container = this.$refs.lottieContainer;
            if (!container) return;

            const path = '/video/cancel-voucher-animation.json';

            try {
                const response = await fetch(path);
                const animData = await response.json();

                // Read active theme CSS variables from :root
                const rootStyle = getComputedStyle(document.documentElement);
                const primaryHex = (rootStyle.getPropertyValue('--primary-color') || '#F27178').trim();

                const hexToRgba = (hex, defaultRgba) => {
                    if (!hex || !hex.startsWith('#')) return defaultRgba;
                    let c = hex.replace('#', '').trim();
                    if (c.length === 3) c = c.split('').map(x => x + x).join('');
                    const num = parseInt(c, 16);
                    if (isNaN(num)) return defaultRgba;
                    return [
                        ((num >> 16) & 255) / 255,
                        ((num >> 8) & 255) / 255,
                        (num & 255) / 255,
                        1
                    ];
                };

                const newPrimary = hexToRgba(primaryHex, [0.949, 0.443, 0.471, 1]);

                // Target primary colors in cancel-voucher-animation.json
                const TARGET_PRIMARY_COLORS = [
                    [0.5176, 0.2588, 0.8588, 1],  // #8442DB
                    [0.2431, 0.3725, 1, 1],       // #3E5FFF
                    [0.9490196078431372, 0.4431372549019608, 0.47058823529411764, 1], // #F27178
                    [0.902, 0, 0, 1]              // #E60000
                ];

                const closeColor = (c1, c2, tol = 0.08) => {
                    if (!Array.isArray(c1) || !Array.isArray(c2) || c1.length < 3) return false;
                    return Math.abs(c1[0] - c2[0]) < tol &&
                           Math.abs(c1[1] - c2[1]) < tol &&
                           Math.abs(c1[2] - c2[2]) < tol;
                };

                const isTargetPrimary = (color) => {
                    return TARGET_PRIMARY_COLORS.some(target => closeColor(color, target));
                };

                const updateColors = (obj) => {
                    if (obj && typeof obj === 'object') {
                        if (obj.c && Array.isArray(obj.c.k) && obj.c.k.length >= 3) {
                            if (isTargetPrimary(obj.c.k)) {
                                obj.c.k = newPrimary;
                            }
                        }
                        for (let k in obj) {
                            if (Object.prototype.hasOwnProperty.call(obj, k)) {
                                updateColors(obj[k]);
                            }
                        }
                    } else if (Array.isArray(obj)) {
                        obj.forEach(item => updateColors(item));
                    }
                };

                updateColors(animData);

                this.animInstance = lottie.loadAnimation({
                    container: container,
                    renderer: 'svg',
                    loop: false,
                    autoplay: true,
                    animationData: animData
                });
            } catch (e) {
                // Fallback to static path loading if fetch fails
                this.animInstance = lottie.loadAnimation({
                    container: container,
                    renderer: 'svg',
                    loop: false,
                    autoplay: true,
                    path: path
                });
            }

            if (this.animInstance) {
                this.animInstance.addEventListener('complete', () => {
                    this.handleVideoEnded();
                });
            }
        },
        destroyLottie() {
            if (this.animInstance) {
                this.animInstance.destroy();
                this.animInstance = null;
            }
        },
        restartVideo() {
            this.initLottie();
        },
        handleVideoEnded() {
            this.$emit('close');
        }
    }
}
</script>

<style scoped>
.cancel-status-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(17, 24, 39, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    z-index: 1200;
}

.cancel-status-card {
    position: relative;
    width: min(360px, 100%);
    background: #ffffff;
    border-radius: 24px;
    padding: 32px 28px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    overflow: hidden;
    box-shadow: 0 24px 60px rgba(15, 23, 42, 0.22);
    border: 2px solid var(--primary-border, var(--primary-light, rgba(242, 113, 120, 0.15)));
}

.cancel-loading,
.cancel-success {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}

.cancel-spinner {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    border: 6px solid var(--secondary-color, #FEE9E9);
    border-top-color: var(--primary-color, #F27178);
    animation: cancelSpin 1s linear infinite;
}

.cancel-message {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #111827;
    text-align: center;
}

.cancel-video {
    width: 132px;
    height: 132px;
    border-radius: 18px;
}

/* Dynamic theme styling for Lottie SVG paths */
.cancel-video :deep(svg path[fill="#F27178"]),
.cancel-video :deep(svg path[fill="rgb(242, 113, 120)"]),
.cancel-video :deep(svg path[fill="#8442DB"]),
.cancel-video :deep(svg path[fill="rgb(132, 66, 219)"]) {
    fill: var(--primary-color, #F27178) !important;
}

.cancel-close {
    margin-top: 8px;
    background: var(--primary-color, #F27178);
    color: #ffffff;
    border: none;
    border-radius: 999px;
    padding: 10px 24px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.cancel-close:hover {
    transform: translateY(-1px);
    opacity: 0.92;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

@keyframes cancelSpin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}
</style>